import { useQuery } from '@tanstack/react-query';
import { TRequestHandlerError } from '@studio-toolkit/request-handler';
import { useKeycloak } from '@studio-toolkit/keycloak';

import { useQuantum } from 'hooks';
import { apiUrl, requestHandler } from 'utils';

export interface ITokenApiResponse {
  status: number;
  message: string;
  token: string;
  expiresAt: string;
}

// Accept an enabled prop to control when the hook should run
const useFetchToken = () => {
  const { trackApiCall } = useQuantum();
  const { keycloakUser } = useKeycloak();

  const url = apiUrl({
    apiEnv: import.meta.env.VITE_APP_ENV,
    endpoint: 'generateToken',
  });

  return useQuery<ITokenApiResponse, TRequestHandlerError>({
    queryKey: ['fetchToken'],
    queryFn: async () => {
      const response = await requestHandler<ITokenApiResponse>({
        requestConfig: {
          method: 'POST',
          url,
          data: { username: keycloakUser?.name ?? '', expirationHours: 0.5 },
        },
        responseHandlers: {
          200: (res) => res,
        },
        errorHandlers: {
          500: (error) => {
            console.log(error);
            throw new Error('Failed to fetch token');
          },
        },
        quantumHandler: trackApiCall,
      });

      const apiRes = response;
      if (!apiRes || !apiRes.token) {
        throw new Error('Token fetch failed');
      }

      return apiRes;
    },
    refetchInterval: (data) => {
      if (!data?.expiresAt) return false;
      const expiresAtMs = Number(data.expiresAt) * 1000;
      const refreshBuffer = 2 * 60 * 1000; // Refresh 2 minutes before expiry
      const timeUntilRefresh = expiresAtMs - Date.now() - refreshBuffer;
      return timeUntilRefresh > 0 ? timeUntilRefresh : false;
    },
    retry: 1,
    refetchOnWindowFocus: false,
  });
};

export default useFetchToken;
